import { z } from '@hono/zod-openapi'
import { KATAKANA_ERROR_MESSAGE, KATAKANA_REGEX } from '../lib/validation_lib'

// id
export const idZ = z
  .string()
  .openapi({ description: '管理者ID', example: 'uuid-1234' })
export type id = z.infer<typeof idZ>

// name
export const nameZ = z
  .string()
  .openapi({ description: '管理者の氏名', example: '田中太郎' })
export type name = z.infer<typeof nameZ>

// nameKana
export const nameKanaZ = z
  .string()
  .max(255)
  .nullable()
  .openapi({ description: '管理者の氏名カナ', example: 'タナカタロウ' })
export type nameKana = z.infer<typeof nameKanaZ>
// 作成・更新リクエスト用。入力時は全角カタカナのみ
export const nameKanaInputZ = z
  .string()
  .max(255)
  .regex(KATAKANA_REGEX, KATAKANA_ERROR_MESSAGE)
  .nullable()
  .openapi({ description: '管理者の氏名カナ', example: 'タナカタロウ' })

// department
export const departmentZ = z
  .string()
  .max(255)
  .nullable()
  .openapi({ description: '所属部署名', example: '営業部' })
export type department = z.infer<typeof departmentZ>

// email
export const emailZ = z.string().openapi({
  description: 'メールアドレス（ログインID）',
  example: 'tanaka@example.com',
})
export type email = z.infer<typeof emailZ>

// emailVerified
export const emailVerifiedZ = z
  .boolean()
  .openapi({ description: 'メールアドレス確認済みかどうか', example: false })
export type emailVerified = z.infer<typeof emailVerifiedZ>

// image
export const imageZ = z
  .string()
  .nullable()
  .openapi({ description: 'プロフィール画像URL', example: 'https://...' })
export type image = z.infer<typeof imageZ>

// role
export const ROLE_SYSTEM_ADMIN = 'system_admin' as const
export const ROLE_SALES = 'sales' as const
export const ROLE_OC_ADMIN = 'oc_admin' as const
export const ROLE_OC_OPERATOR_DAY = 'oc_operator_day' as const
export const ROLE_OC_OPERATOR_EMERGENCY = 'oc_operator_emergency' as const

// シード用の既定値であり、これ以外のカスタムロールも使用できる。
export const ADMIN_ROLES = [
  ROLE_SYSTEM_ADMIN,
  ROLE_SALES,
  ROLE_OC_ADMIN,
  ROLE_OC_OPERATOR_DAY,
  ROLE_OC_OPERATOR_EMERGENCY,
] as const

/** ロール名として使える文字。member.role に文字列で入るため記号は許さない。 */
export const ROLE_NAME_PATTERN = /^[a-z][a-z0-9_]*$/

export const roleZ = z
  .string()
  .min(1)
  .max(255)
  .regex(
    ROLE_NAME_PATTERN,
    'ロール名は英小文字で始まり、英小文字・数字・アンダースコアのみ使用できます',
  )
  .openapi({
    description:
      '権限ロール。組織ごとに定義される。既定値は system_admin:システム管理者 / sales:営業 / oc_admin:OC管理者 / oc_operator_day:OCオペレーター(日中) / oc_operator_emergency:OCオペレーター(緊急)',
    example: ROLE_OC_OPERATOR_DAY,
  })
export type role = z.infer<typeof roleZ>

// roleLabel
export const roleLabelZ = z.string().min(1).max(255).openapi({
  description: 'ロールの表示名。画面に出るのはこちらでロール名ではない',
  example: 'OCオペレータ（日中）',
})
export type roleLabel = z.infer<typeof roleLabelZ>

// banned
export const bannedZ = z
  .boolean()
  .nullable()
  .openapi({ description: '利用停止中かどうか', example: false })
export type banned = z.infer<typeof bannedZ>

// banReason
export const banReasonZ = z
  .string()
  .nullable()
  .openapi({ description: '利用停止の理由', example: '不正利用のため' })
export type banReason = z.infer<typeof banReasonZ>

// banExpires
export const banExpiresStringZ = z.string().nullable().openapi({
  description: '利用停止の解除日時（ISO8601）',
  example: '2024-12-31T23:59:59.000Z',
})
export type banExpiresString = z.infer<typeof banExpiresStringZ>

export const banExpiresDateZ = z
  .date()
  .nullable()
  .openapi({ example: '2024-12-31T23:59:59.000Z' })
export type banExpiresDate = z.infer<typeof banExpiresDateZ>

// createdAt
export const createdAtStringZ = z.string().openapi({
  description: '作成日時（ISO8601）',
  example: '2024-01-01T00:00:00.000Z',
})
export type createdAtString = z.infer<typeof createdAtStringZ>

export const createdAtDateZ = z
  .date()
  .openapi({ example: '2024-01-01T00:00:00.000Z' })
export type createdAtDate = z.infer<typeof createdAtDateZ>

// updatedAt
export const updatedAtStringZ = z.string().openapi({
  description: '更新日時（ISO8601）',
  example: '2024-01-01T00:00:00.000Z',
})
export type updatedAtString = z.infer<typeof updatedAtStringZ>

export const updatedAtDateZ = z
  .date()
  .openapi({ example: '2024-01-01T00:00:00.000Z' })
export type updatedAtDate = z.infer<typeof updatedAtDateZ>

// deletedAt
export const deletedAtStringZ = z.string().nullable().openapi({
  description: '削除日時（ISO8601）。null は未削除',
  example: '2024-01-01T00:00:00.000Z',
})
export type deletedAtString = z.infer<typeof deletedAtStringZ>

export const deletedAtDateZ = z
  .date()
  .nullable()
  .openapi({ example: '2024-01-01T00:00:00.000Z' })
export type deletedAtDate = z.infer<typeof deletedAtDateZ>
